import { getCollectionInventoryByHandle } from '@api/inventory'
import { getProductsWithUpdatedVariants } from '@helpers/filters/updateVariants'
import trackError from '@helpers/trackError'
import { useEffect, useState } from 'react'
import { Collection } from 'types/collection'

function getCollectionsWithFilteredVariants(
  collections: Collection[] = [],
  variants = []
) {
  return collections.map(collection => {
    const items = getProductsWithUpdatedVariants(collection.items, variants)
    return {
      ...collection,
      items
    }
  })
}

export function useFetchVariants({
  collectionHandle,
  collections
}: {
  collectionHandle: string
  collections: Collection[]
}) {
  // On first render use SSG data
  const [{ updatedCollections, inventoryReady, variantExist }, setState] =
    useState(() => ({
      updatedCollections: collections,
      inventoryReady: false,
      variantExist: false
    }))

  useEffect(() => {
    let cancelled = false

    const fetchVariants = async () => {
      try {
        const variants = await getCollectionInventoryByHandle(collectionHandle)
        const filteredCollection = getCollectionsWithFilteredVariants(
          collections,
          variants.collectionInventory.products
        )
        if (cancelled) return
        const hasItemsWithVariants = filteredCollection.some(c =>
          c.items.some(i => (i.variants?.length ?? 0) > 0)
        )

        setState({
          updatedCollections: filteredCollection,
          inventoryReady: true,
          variantExist: hasItemsWithVariants
        })
      } catch (e) {
        if (cancelled) return
        const variantsError = new Error(
          'Collection Product fetchVariants failed'
        )
        const error = e instanceof Error ? e : variantsError
        trackError(variantsError, { error })

        setState(prev => ({
          ...prev,
          inventoryReady: true,
          variantExist: false
        }))
      }
    }

    if (collectionHandle) fetchVariants()

    return () => {
      cancelled = true
    }
  }, [collectionHandle])

  return { updatedCollections, inventoryReady, variantExist }
}
